/**
 * 법령 요율 Firestore 동기화 서비스
 *
 * - `syncOnce`: 부트스트랩 시 1회 호출. cache → Firestore (5초 타임아웃) 순.
 * - `startWatching`: 실시간 구독. 관리자가 Firestore 값 변경 시 모든 구독 컴포넌트 즉시 리렌더.
 * - `forceRefresh`: 사용자/관리자 수동 트리거.
 *
 * 모든 함수는 throw 하지 않는다 — Firestore 실패는 default fallback 으로 dégrade. 콘솔 경고만.
 */

import { doc, getDoc, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { db } from '../firebaseService';
import { useRatesStore } from '../../store/ratesStore';
import { loadFromCache, saveToCache } from './rateCache';
import type { RegulatoryRates } from '../../types/regulatoryRates';

/** Firestore 도큐먼트 경로: `config/rates/{year}/current` */
function ratesDocRef(year: number) {
  return doc(db, 'config', 'rates', String(year), 'current');
}

/** Firestore 데이터에 meta 보강 (누락 대비) */
function withMeta(data: RegulatoryRates, year: number): RegulatoryRates {
  const existing = data.meta;
  return {
    ...data,
    meta: {
      ...(existing ?? { country: 'KR', schemaVersion: 1 }),
      year,
    },
  };
}

/** 캐시 vs Firestore 데이터 비교 — 더 새 것이면 true */
function isNewerThanCache(fresh: RegulatoryRates, cached: RegulatoryRates | null): boolean {
  if (!cached) return true;
  const freshVer = fresh.meta?.schemaVersion ?? 0;
  const cachedVer = cached.meta?.schemaVersion ?? 0;
  if (freshVer > cachedVer) return true;
  const freshAt = fresh.meta?.publishedAt ? new Date(fresh.meta.publishedAt).getTime() : 0;
  const cachedAt = cached.meta?.publishedAt ? new Date(cached.meta.publishedAt).getTime() : 0;
  return freshAt > cachedAt;
}

let watchUnsub: Unsubscribe | null = null;

/**
 * 부트스트랩 시 한 번 호출.
 * 1) localStorage cache → store 즉시 반영 (Firestore 도달 전 UX 안정)
 * 2) Firestore getDoc (5초 타임아웃) → 더 새 버전이면 store + cache 갱신
 */
export async function syncOnce(year = new Date().getFullYear()): Promise<void> {
  const store = useRatesStore.getState();
  store.setLoading(true);

  const cached = loadFromCache(year);
  if (cached) {
    store.setCurrent(cached);
  }

  try {
    const ref = ratesDocRef(year);
    const snap = await Promise.race([
      getDoc(ref),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('rateSync: Firestore timeout (5s)')), 5000),
      ),
    ]);

    if (!snap.exists()) {
      console.warn(`[rateSync] config/rates/${year}/current not found — default fallback 유지`);
      return;
    }

    const data = withMeta(snap.data() as RegulatoryRates, year);
    console.info(
      `[rateSync] Loaded ${year} from Firestore. socialInsurance.nps.workerShare =`,
      data.socialInsurance?.nps?.workerShare,
      '/ nhi.workerShare =',
      data.socialInsurance?.nhi?.workerShare,
      '/ ei.unemployment.worker =',
      data.socialInsurance?.ei?.unemployment?.worker,
    );
    if (isNewerThanCache(data, cached)) {
      useRatesStore.getState().setCurrent(data);
      saveToCache(year, data);
    }
  } catch (e) {
    console.warn('[rateSync] syncOnce failed (default fallback 유지):', e);
  } finally {
    useRatesStore.getState().setLoading(false);
  }
}

/**
 * 실시간 구독 시작. 관리자가 Firestore 값 변경 시 모든 사용처 즉시 리렌더.
 * 동일 year 로 재호출 시 기존 구독 정리 후 새로 구독.
 */
export function startWatching(year = new Date().getFullYear()): Unsubscribe {
  stopWatching();
  const ref = ratesDocRef(year);
  watchUnsub = onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) return;
      const data = withMeta(snap.data() as RegulatoryRates, year);
      useRatesStore.getState().setCurrent(data);
      saveToCache(year, data);
    },
    (err) => {
      console.warn('[rateSync] startWatching error:', err);
    },
  );
  return watchUnsub;
}

export function stopWatching(): void {
  if (watchUnsub) {
    watchUnsub();
    watchUnsub = null;
  }
}

/** 사용자/관리자 수동 새로고침 */
export async function forceRefresh(year = new Date().getFullYear()): Promise<boolean> {
  try {
    const snap = await getDoc(ratesDocRef(year));
    if (!snap.exists()) return false;
    const data = withMeta(snap.data() as RegulatoryRates, year);
    useRatesStore.getState().setCurrent(data);
    saveToCache(year, data);
    return true;
  } catch (e) {
    console.warn('[rateSync] forceRefresh failed:', e);
    return false;
  }
}
